import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const closeAll = () => {
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
    setDropdownOpen(false);
  };

  const navLinkClass = scrolled
    ? "text-foreground-700 hover:text-foreground-950"
    : "text-white/85 hover:text-white";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200"
          : "bg-black/20 backdrop-blur-sm"
      }`}
    >
      <div className="mx-auto flex items-center justify-between px-6 md:px-10 py-3.5">
        <Link
          to="/"
          onClick={closeAll}
          className={`text-lg font-bold font-heading tracking-wide whitespace-nowrap cursor-pointer ${
            scrolled ? "text-foreground-950" : "text-white"
          }`}
        >
          OOPBuy Spreadsheet
        </Link>

        <div className="hidden md:flex items-center gap-5">
          <Link
            to="/"
            onClick={closeAll}
            className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${navLinkClass}`}
          >
            <i className="ri-home-4-line mr-1.5"></i>
            Home
          </Link>

          <Link
            to="/article/what-is-oopbuy-spreadsheet"
            onClick={closeAll}
            className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${navLinkClass}`}
          >
            <i className="ri-book-open-line mr-1.5"></i>
            Shopping Guide
          </Link>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${navLinkClass}`}
          >
            <i className="ri-shopping-bag-3-line mr-1.5"></i>
            Shop Now
          </a>
        </div>

        <div className="flex items-center gap-2.5">
          <div className="relative hidden md:block" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                scrolled
                  ? "bg-background-100 text-foreground-800 hover:bg-background-200 border border-background-200"
                  : "bg-white/12 text-white hover:bg-white/22 backdrop-blur-sm"
              }`}
            >
              <i className="ri-grid-line"></i>
              <span>Categories</span>
              <i
                className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                  dropdownOpen ? "rotate-180" : ""
                }`}
              ></i>
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-60 bg-background-50 rounded-xl shadow-lg border border-background-200 py-2 max-h-80 overflow-y-auto z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                      <img src={cat.imageUrlSmall} alt={cat.name} className="w-full h-full object-cover" />
                    </div>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className={`hidden md:inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all ${
              scrolled
                ? "bg-primary-500 text-white hover:bg-primary-600"
                : "bg-white/18 text-white hover:bg-white/28 backdrop-blur-sm"
            }`}
          >
            oopbuy spreadsheet
            <i className="ri-arrow-right-up-line"></i>
          </a>

          <button
            onClick={() => { setMobileMenuOpen(!mobileMenuOpen); setMobileCatOpen(false); }}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
              scrolled
                ? "text-foreground-800 hover:bg-background-100"
                : "text-white hover:bg-white/15"
            }`}
            aria-label="Menu"
          >
            <i className={`text-lg ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className={`md:hidden border-t ${scrolled ? "border-background-200 bg-background-50" : "border-white/15 bg-black/30 backdrop-blur-md"}`}>
          <div className="px-6 py-4 space-y-1">
            <Link
              to="/"
              onClick={closeAll}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                scrolled ? "text-foreground-700 hover:bg-background-100" : "text-white hover:bg-white/10"
              }`}
            >
              <i className="ri-home-4-line"></i>
              Home
            </Link>
            <Link
              to="/article/what-is-oopbuy-spreadsheet"
              onClick={closeAll}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                scrolled ? "text-foreground-700 hover:bg-background-100" : "text-white hover:bg-white/10"
              }`}
            >
              <i className="ri-book-open-line"></i>
              Shopping Guide
            </Link>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeAll}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                scrolled ? "text-foreground-700 hover:bg-background-100" : "text-white hover:bg-white/10"
              }`}
            >
              <i className="ri-shopping-bag-3-line"></i>
              Shop Now
            </a>

            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                scrolled ? "text-foreground-700 hover:bg-background-100" : "text-white hover:bg-white/10"
              }`}
            >
              <span className="flex items-center gap-2.5">
                <i className="ri-grid-line"></i>
                Categories
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}></i>
            </button>

            {mobileCatOpen && (
              <div className="ml-3 pl-3 border-l-2 border-background-200 space-y-0.5 mt-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeAll}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-foreground-600 hover:text-primary-600 hover:bg-primary-50/50 cursor-pointer transition-colors"
                  >
                    <div className="w-7 h-7 rounded-md overflow-hidden flex-shrink-0 bg-background-100">
                      <img src={cat.imageUrlSmall} alt={cat.name} className="w-full h-full object-cover" />
                    </div>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <div className="pt-2">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeAll}
                className={`flex items-center justify-center gap-1.5 w-full px-4 py-2.5 rounded-full text-sm font-semibold cursor-pointer transition-all ${
                  scrolled
                    ? "bg-primary-500 text-white"
                    : "bg-white/18 text-white"
                }`}
              >
                oopbuy spreadsheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}